import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  ConstrainedDialog as Dialog,
  ConstrainedDialogContent as DialogContent,
  ConstrainedDialogDescription as DialogDescription,
  ConstrainedDialogFooter as DialogFooter,
  ConstrainedDialogHeader as DialogHeader,
  ConstrainedDialogTitle as DialogTitle,
} from "../../ui/constrained-dialog";
import { Button } from "../../ui/button";
import { Textarea } from "../../ui/textarea";
import { Alert, AlertDescription } from "../../ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Input } from "../../ui/input";
import {
  Loader2,
  Upload,
  CheckCircle,
  AlertCircle,
  FileText,
  Eraser,
  Search,
  ChevronUp,
  ChevronDown,
  X,
  File,
  Info,
  Sparkles,
} from "lucide-react";
import {
  transcriptService,
  Transcript,
} from "../../../services/transcriptService";
import { Interview } from "../../../services/interviewService";
import { toast } from "../../../hooks/use-toast";
import { track } from "@vercel/analytics";
import * as pdfjsLib from "pdfjs-dist";
import mammoth from "mammoth";

// Configure PDF.js worker - use local file from public folder
if (typeof window !== "undefined") {
  pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
}

interface TranscriptUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  interview: Interview;
  onUploadSuccess: () => void;
  mode?: "upload" | "view"; // New prop to determine if uploading or viewing
  existingTranscript?: Transcript; // Existing transcript data for viewing
  onOpenChat?: (interviewId?: string) => void; // Callback to open chat sidebar
}

export function TranscriptUploadModal({
  isOpen,
  onClose,
  interview,
  onUploadSuccess,
  mode = "upload",
  existingTranscript,
  onOpenChat,
}: TranscriptUploadModalProps) {
  const [rawText, setRawText] = useState("");
  const [editedOriginalText, setEditedOriginalText] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [hasEditedOriginal, setHasEditedOriginal] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    success: boolean;
    message?: string;
    error?: string;
    change_summary?: string;
    word_count_raw?: number;
    word_count_clean?: number;
  } | null>(null);

  // File upload states
  const [isExtracting, setIsExtracting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Search functionality
  const [searchQuery, setSearchQuery] = useState("");
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<"original" | "cleaned">(
    "original"
  );

  const isViewMode = mode === "view";
  const hasCleanedTranscript = existingTranscript?.text_clean;

  // Ref for cleaned container
  const cleanedContainerRef = useRef<HTMLDivElement>(null);

  const clearSearch = () => {
    setSearchQuery("");
    setCurrentMatchIndex(0);
  };

  // Initialize edited text when existingTranscript changes
  React.useEffect(() => {
    if (existingTranscript?.text_raw) {
      const formattedText = formatTranscriptText(existingTranscript.text_raw);
      setEditedOriginalText(formattedText);
      setHasEditedOriginal(false);
    }
  }, [existingTranscript]);

  // Set initial active tab based on available content
  useEffect(() => {
    if (hasCleanedTranscript) {
      setActiveTab("cleaned");
    } else {
      setActiveTab("original");
    }
  }, [hasCleanedTranscript]);

  // Clear search when switching away from cleaned tab
  useEffect(() => {
    if (activeTab !== "cleaned") {
      clearSearch();
    }
  }, [activeTab]);

  // This will be defined after searchMatches

  // Extract text from PDF file
  const extractTextFromPDF = async (file: File): Promise<string> => {
    try {
      setIsExtracting(true);
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let text = "";

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();

        // Reconstruct lines by grouping items by Y (rounded) and ordering by X
        type PdfItem = { str: string; transform?: number[]; hasEOL?: boolean };
        const items = textContent.items as PdfItem[];
        const linesMap = new Map<number, Array<{ x: number; text: string }>>();

        for (const item of items) {
          const transform = item.transform || [];
          const x =
            typeof transform[4] === "number" ? (transform[4] as number) : 0;
          const yRaw =
            typeof transform[5] === "number" ? (transform[5] as number) : 0;
          const yKey = Math.round(yRaw);

          if (!linesMap.has(yKey)) {
            linesMap.set(yKey, []);
          }
          linesMap.get(yKey)!.push({ x, text: item.str });

          if (item.hasEOL) {
            // Encourage a break at this Y by appending a high-x sentinel newline segment
            linesMap
              .get(yKey)!
              .push({ x: Number.MAX_SAFE_INTEGER, text: "\n" });
          }
        }

        const yKeys = Array.from(linesMap.keys()).sort((a, b) => b - a); // PDF origin bottom-left

        // Build paragraphs by detecting spacing between lines
        const paragraphs: string[] = [];
        let currentParagraph: string[] = [];
        let previousY: number | null = null;

        for (let i = 0; i < yKeys.length; i++) {
          const yKey = yKeys[i];
          const segments = linesMap.get(yKey)!;
          segments.sort((a, b) => a.x - b.x);
          const lineText = segments
            .map((s) => s.text)
            .join(" ")
            .replace(/\s+/g, " ")
            .replace(/\s*\n\s*/g, "\n")
            .trim();

          if (lineText.length === 0) continue;

          // Check if this is a paragraph break (large Y gap)
          if (previousY !== null) {
            const yDiff = Math.abs(previousY - yKey);
            // Normal line spacing is ~10-20 units, paragraph breaks are typically >25
            if (yDiff > 25) {
              // Save current paragraph and start a new one
              if (currentParagraph.length > 0) {
                paragraphs.push(currentParagraph.join(" "));
                currentParagraph = [];
              }
            }
          }

          // Add line to current paragraph
          currentParagraph.push(lineText);
          previousY = yKey;
        }

        // Don't forget the last paragraph
        if (currentParagraph.length > 0) {
          paragraphs.push(currentParagraph.join(" "));
        }

        text += paragraphs.join("\n\n") + "\n\n"; // blank line between pages
      }

      return text;
    } catch (error) {
      console.error("Error extracting text from PDF:", error);
      throw new Error("Failed to extract text from PDF file");
    } finally {
      setIsExtracting(false);
    }
  };

  // Extract text from DOCX file
  const extractTextFromDOCX = async (file: File): Promise<string> => {
    try {
      setIsExtracting(true);
      const arrayBuffer = await file.arrayBuffer();

      // Use convertToHtml to preserve ALL paragraph and line break structure
      const result = await mammoth.convertToHtml({ arrayBuffer });

      // Convert HTML to text while preserving all breaks
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = result.value;

      // Process all elements to preserve structure
      const processNode = (node: Node): string => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent || "";
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          const tagName = element.tagName.toLowerCase();

          // Block elements get double newlines
          if (tagName === "p" || tagName === "div") {
            const content = Array.from(element.childNodes)
              .map(processNode)
              .join("");
            return content.trim() ? content.trim() + "\n\n" : "";
          }

          // Line breaks get single newline
          if (tagName === "br") {
            return "\n";
          }

          // Process children for other elements
          return Array.from(element.childNodes).map(processNode).join("");
        }

        return "";
      };

      const text = processNode(tempDiv);
      return text.trim();
    } catch (error) {
      console.error("Error extracting text from DOCX:", error);
      throw new Error("Failed to extract text from DOCX file");
    } finally {
      setIsExtracting(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    try {
      if (!file) return;

      let extractedText = "";

      // Determine file type and extract text
      if (file.name.endsWith(".pdf") || file.type === "application/pdf") {
        extractedText = await extractTextFromPDF(file);
      } else if (
        file.name.endsWith(".docx") ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ) {
        extractedText = await extractTextFromDOCX(file);
      } else if (file.name.endsWith(".txt") || file.type === "text/plain") {
        // Read .txt files directly
        extractedText = await file.text();
      } else {
        toast({
          title: "Unsupported File Type",
          description: "Please upload a .pdf, .docx, or .txt file",
          variant: "destructive",
        });
        return;
      }

      // Populate the textarea with normalized extracted text
      setRawText(normalizeExtractedText(extractedText));

      // Show success message
      toast({
        title: "File Extracted",
        description: `Successfully extracted ${
          extractedText.split(/\s+/).length
        } words from ${file.name}`,
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Error handling file upload:", error);
      toast({
        title: "Extraction Failed",
        description: error.message || "Failed to extract text from file",
        variant: "destructive",
      });
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle drag and drop
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleUpload = async (textToUpload?: string) => {
    const textToUse = textToUpload || rawText;

    if (!textToUse.trim()) {
      setUploadResult({
        success: false,
        error: "Please enter transcript text",
      });
      return;
    }

    setIsUploading(true);
    setUploadResult(null);

    try {
      const result = await transcriptService.uploadTranscript(
        interview.id,
        textToUse.trim()
      );

      if (result.success) {
        // Track transcript upload
        track("transcript_uploaded", {
          interview_id: interview.id,
          word_count: textToUse.trim().split(/\s+/).length,
          is_reupload: !!existingTranscript,
        });

        // Show success toast and close modal immediately
        toast({
          title: "Upload Started",
          description:
            "Transcript is being processed in the background. This may take up to 5 minutes.",
          duration: 3000,
        });

        // Update the interview status immediately to show "processing"
        onUploadSuccess();

        // Close the modal immediately
        onClose();
        setRawText("");
        setEditedOriginalText("");
        setHasEditedOriginal(false);
        setUploadResult(null);
      } else {
        setUploadResult(result);
        toast({
          title: "Upload Failed",
          description: result.error || "Failed to upload transcript",
          variant: "destructive",
        });
      }
    } catch (error) {
      setUploadResult({
        success: false,
        error: "Failed to upload transcript",
      });
      toast({
        title: "Upload Failed",
        description: "Failed to upload transcript",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleReUpload = () => {
    handleUpload(editedOriginalText);
  };

  const handleOriginalTextChange = (newText: string) => {
    setEditedOriginalText(newText);
    const originalFormatted = formatTranscriptText(
      existingTranscript?.text_raw || ""
    );
    setHasEditedOriginal(newText !== originalFormatted);
  };

  const handleClose = () => {
    // Show notification if closing during upload
    if (isUploading) {
      toast({
        title: "Processing in background",
        description:
          "Your transcript is being cleaned. You can check the status in the interview table.",
        duration: 5000,
      });
    }

    // Allow closing even during upload - processing continues in background
    onClose();
    setRawText("");
    setUploadResult(null);
    setIsUploading(false); // Reset upload state when closing
    clearSearch(); // Clear search when closing
  };

  const wordCount = rawText
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;

  // Helper function to format transcript text
  const formatTranscriptText = (text: string | undefined) => {
    if (!text) return "";

    // Handle legacy data that might still be JSON (for existing transcripts)
    if (text.trim().startsWith("{") && text.trim().endsWith("}")) {
      try {
        const parsed = JSON.parse(text);
        if (parsed.cleaned_text) {
          text = parsed.cleaned_text;
        }
      } catch {
        // If JSON parsing fails, use text as-is
      }
    }

    // Normalize speaker formatting to handle OpenAI inconsistencies
    // Fix cases where speaker labels are on separate lines from content
    let normalizedText = text.trim();

    // Pattern: "Interviewer:\n" or "Expert:\n" followed by content on next line
    normalizedText = normalizedText.replace(
      /^((?:Interviewer|Expert):\s*)\n+(\S)/gm,
      "$1 $2"
    );

    // Ensure consistent double line breaks between speaker turns for readability
    // Replace multiple consecutive line breaks with double line breaks
    normalizedText = normalizedText.replace(/\n{3,}/g, "\n\n");

    return normalizedText;
  };

  // Normalize freshly extracted text - preserve structure, just clean up spacing
  const normalizeExtractedText = (text: string) => {
    if (!text) return "";

    // Normalize line endings
    let t = text.replace(/\r\n?/g, "\n");

    // Collapse excessive blank lines (more than 2) to maintain readability
    t = t.replace(/\n{3,}/g, "\n\n");

    // Trim trailing spaces per line
    t = t
      .split("\n")
      .map((line) => line.replace(/\s+$/g, ""))
      .join("\n");

    return t.trim();
  };

  // Search functionality - only for cleaned tab
  const searchMatches = useMemo(() => {
    if (
      !searchQuery.trim() ||
      !hasCleanedTranscript ||
      !existingTranscript?.text_clean
    )
      return [];

    const matches: Array<{
      index: number;
      text: string;
      lineIndex: number;
      tab: "cleaned";
    }> = [];

    const text = formatTranscriptText(existingTranscript.text_clean);
    const lines = text.split("\n");
    const query = searchQuery.toLowerCase();

    lines.forEach((line, lineIndex) => {
      const lowerLine = line.toLowerCase();
      let startIndex = 0;

      while (true) {
        const matchIndex = lowerLine.indexOf(query, startIndex);
        if (matchIndex === -1) break;

        matches.push({
          index: matchIndex,
          text: line.substring(matchIndex, matchIndex + searchQuery.length),
          lineIndex,
          tab: "cleaned",
        });

        startIndex = matchIndex + 1;
      }
    });

    return matches;
  }, [searchQuery, existingTranscript?.text_clean, hasCleanedTranscript]);

  // Scroll to current match when navigating
  useEffect(() => {
    if (
      searchMatches.length === 0 ||
      !searchQuery ||
      !cleanedContainerRef.current
    )
      return;

    const currentMatch = searchMatches[currentMatchIndex];
    if (!currentMatch) return;

    const scrollToMatch = () => {
      const container = cleanedContainerRef.current;
      if (!container) return;

      const lines = container.children;
      if (lines[currentMatch.lineIndex]) {
        const targetLine = lines[currentMatch.lineIndex] as HTMLElement;

        // Add faster smooth scroll style to container
        container.style.scrollBehavior = "smooth";
        container.style.setProperty("scroll-behavior", "smooth");

        targetLine.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // Reset scroll behavior after a shorter duration for faster animation
        setTimeout(() => {
          container.style.scrollBehavior = "auto";
        }, 200);
      }
    };

    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(scrollToMatch, 50);
    return () => clearTimeout(timeoutId);
  }, [currentMatchIndex, searchMatches, searchQuery]);

  const highlightText = (text: string, query: string, lineIndex: number) => {
    if (!query.trim()) return text;

    const regex = new RegExp(
      `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === query.toLowerCase()) {
        // Check if this line contains the current match
        const currentMatch = searchMatches[currentMatchIndex];
        const isCurrentMatchLine =
          currentMatch && currentMatch.lineIndex === lineIndex;

        return (
          <mark
            key={index}
            className={
              isCurrentMatchLine
                ? "bg-orange-200 dark:bg-orange-800/70 text-inherit rounded px-0.5"
                : "bg-yellow-100 dark:bg-yellow-800/40 text-inherit rounded px-0.5"
            }
          >
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  const handleSearchNavigation = (direction: "next" | "prev") => {
    if (searchMatches.length === 0) return;

    if (direction === "next") {
      setCurrentMatchIndex((prev) => (prev + 1) % searchMatches.length);
    } else {
      setCurrentMatchIndex(
        (prev) => (prev - 1 + searchMatches.length) % searchMatches.length
      );
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && searchMatches.length > 0) {
      e.preventDefault();
      handleSearchNavigation("next");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-6xl max-h-[90vh] flex flex-col overflow-hidden pb-3">
        <DialogHeader className="flex-shrink-0 flex flex-row items-start justify-between">
          <div className="flex-1">
            <DialogTitle>
              {isViewMode ? "View Transcript" : "Upload Transcript"}
            </DialogTitle>
            <DialogDescription>
              {isViewMode
                ? "Transcript for interview with"
                : "Upload transcript for interview with"}{" "}
              <span className="font-medium">
                {interview.expertFirstName} {interview.expertLastName}
              </span>
              {interview.company && <span> at {interview.company}</span>}
            </DialogDescription>
          </div>
          {isViewMode && hasCleanedTranscript && onOpenChat && (
            <Button
              onClick={() => {
                onOpenChat(interview.id);
                handleClose();
              }}
              variant="outline"
              size="sm"
              className="gap-2 mr-5 border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-950 dark:hover:text-blue-300"
            >
              <Sparkles className="h-4 w-4" />
              Ask AI
            </Button>
          )}
        </DialogHeader>

        {/* Fixed tabs area - outside scroll */}
        {isViewMode && (
          <div className="flex-shrink-0 space-y-4">
            <Tabs
              defaultValue={hasCleanedTranscript ? "cleaned" : "original"}
              value={activeTab}
              onValueChange={(value) =>
                setActiveTab(value as "original" | "cleaned")
              }
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger
                  value="original"
                  className="flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  Original
                </TabsTrigger>
                <TabsTrigger
                  value="cleaned"
                  className="flex items-center gap-2 data-[state=active]:bg-green-50 data-[state=active]:border-green-200 data-[state=active]:text-green-800 dark:data-[state=active]:bg-green-900/20 dark:data-[state=active]:border-green-800 dark:data-[state=active]:text-green-200"
                  disabled={!hasCleanedTranscript}
                >
                  <Eraser className="h-4 w-4 text-green-600 dark:text-green-400" />
                  Cleaned
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Search bar - only show for cleaned tab */}
            {hasCleanedTranscript && activeTab === "cleaned" && (
              <div className="flex items-center gap-2 px-1">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search transcript..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className="pl-9 pr-20"
                  />
                  {searchQuery && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">
                        {searchMatches.length > 0
                          ? `${currentMatchIndex + 1}/${searchMatches.length}`
                          : "0/0"}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearSearch}
                        className="h-6 w-6 p-0"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
                {searchQuery && searchMatches.length > 0 && (
                  <div className="flex">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSearchNavigation("prev")}
                      className="h-8 w-8 p-0 hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSearchNavigation("next")}
                      className="h-8 w-8 p-0 hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Scrollable content area */}
        <div className="flex-1 min-h-0 overflow-y-auto space-y-2">
          {isViewMode ? (
            // View mode - content based on active tab (tabs are now above)
            <>
              {activeTab === "original" && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium">
                      Original Transcript
                    </label>
                    {hasEditedOriginal && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-orange-600 dark:text-orange-400">
                          Text modified
                        </span>
                        <Button
                          size="sm"
                          onClick={handleReUpload}
                          disabled={isUploading}
                          className="h-7 gap-1 text-xs"
                        >
                          {isUploading ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Re-uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-3 w-3" />
                              Re-upload & Clean
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="min-h-[500px] max-h-[500px] border border-gray-300 dark:border-gray-600 rounded-md bg-background overflow-hidden">
                    <Textarea
                      value={editedOriginalText}
                      onChange={(e) => handleOriginalTextChange(e.target.value)}
                      className="min-h-[500px] resize-none whitespace-pre-wrap border-0 rounded-none focus-visible:ring-0"
                      placeholder="Edit the original transcript text..."
                    />
                  </div>
                </div>
              )}

              {activeTab === "cleaned" && (
                <>
                  {hasCleanedTranscript ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium flex items-center gap-2">
                          <span className="text-green-700 dark:text-green-300">
                            Cleaned Transcript
                          </span>
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 rounded-full">
                            AI Enhanced
                          </span>
                        </label>
                        {existingTranscript?.meta?.change_summary && (
                          <p className="text-xs text-muted-foreground">
                            {existingTranscript.meta.change_summary}
                          </p>
                        )}
                      </div>
                      <div className="min-h-[500px] max-h-[500px] border border-gray-200 dark:border-gray-700 rounded-md bg-background overflow-hidden">
                        <div
                          ref={cleanedContainerRef}
                          className="p-6 overflow-y-auto text-sm leading-relaxed h-full"
                          style={{ maxHeight: "500px" }}
                        >
                          {formatTranscriptText(existingTranscript?.text_clean)
                            .split("\n")
                            .map((line, index, lines) => {
                              const trimmedLine = line.trim();
                              const isInterviewerLine = trimmedLine
                                .toLowerCase()
                                .startsWith("interviewer:");
                              const isExpertLine = trimmedLine
                                .toLowerCase()
                                .startsWith("expert:");
                              const isEmpty = trimmedLine === "";

                              // Get previous line to detect speaker changes
                              const prevLine =
                                index > 0 ? lines[index - 1].trim() : "";
                              const prevIsInterviewer = prevLine
                                .toLowerCase()
                                .startsWith("interviewer:");
                              const prevIsExpert = prevLine
                                .toLowerCase()
                                .startsWith("expert:");
                              const isSpeakerChange =
                                (isInterviewerLine && !prevIsInterviewer) ||
                                (isExpertLine && !prevIsExpert);

                              // Skip empty lines but add spacing
                              if (isEmpty) {
                                return <div key={index} className="h-3" />;
                              }

                              // Regular text lines (not speaker lines)
                              if (!isInterviewerLine && !isExpertLine) {
                                return (
                                  <p
                                    key={index}
                                    className="mb-3 text-foreground"
                                  >
                                    {searchQuery
                                      ? highlightText(
                                          line || "\u00A0",
                                          searchQuery,
                                          index
                                        )
                                      : line || "\u00A0"}
                                  </p>
                                );
                              }

                              // Speaker lines with clean styling
                              return (
                                <div key={index} className="mb-4">
                                  {isSpeakerChange && index > 0 && (
                                    <div className="h-4" />
                                  )}
                                  <div>
                                    <div className="mb-1.5">
                                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                        {isInterviewerLine
                                          ? "Interviewer"
                                          : "Expert"}
                                      </span>
                                    </div>
                                    <div>
                                      <p className="mb-0 text-foreground">
                                        {searchQuery
                                          ? highlightText(
                                              trimmedLine.replace(
                                                /^(interviewer|expert):\s*/i,
                                                ""
                                              ),
                                              searchQuery,
                                              index
                                            )
                                          : trimmedLine.replace(
                                              /^(interviewer|expert):\s*/i,
                                              ""
                                            )}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center min-h-[500px] text-muted-foreground">
                      <div className="text-center">
                        <Eraser className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No cleaned transcript available</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            // Upload mode - two panel layout
            <div className="grid grid-cols-[1fr_auto_1fr] gap-0">
              {/* Left Panel - File Upload */}
              <div className="space-y-2 pr-4">
                <label className="block text-sm font-medium">Upload File</label>
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`
                    border-2 border-dashed rounded-lg p-6 transition-colors
                    ${
                      dragActive
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-950/20"
                        : "border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600"
                    }
                    ${
                      isExtracting
                        ? "opacity-50 pointer-events-none"
                        : "cursor-pointer"
                    }
                  `}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.txt"
                    onChange={handleFileInputChange}
                    className="hidden"
                    disabled={isExtracting}
                  />
                  <div className="flex flex-col items-center justify-center space-y-3 text-center">
                    {isExtracting ? (
                      <>
                        <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />
                        <p className="text-sm text-muted-foreground">
                          Extracting text...
                        </p>
                      </>
                    ) : (
                      <>
                        <File className="h-12 w-12 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium">
                            Click to upload or drag and drop
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            PDF, DOCX, or TXT files
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* File upload info */}
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>• Supported formats: PDF, DOCX, TXT</p>
                  <p>• Text will be extracted and shown in the text area</p>
                </div>
              </div>

              {/* Vertical Divider */}
              <div className="w-px bg-gray-300 dark:bg-gray-700 mx-4" />

              {/* Right Panel - Text Input */}
              <div className="space-y-2 pl-4">
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <label
                      htmlFor="transcript-text"
                      className="block text-sm font-medium mb-1"
                    >
                      Transcript Text
                    </label>
                    <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md mb-2">
                      <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-blue-800 dark:text-blue-300">
                        <strong>Tip:</strong> Raw, unformatted transcripts are
                        fine. Our AI will automatically clean, format, and
                        structure it.
                      </p>
                    </div>
                  </div>
                </div>
                <Textarea
                  id="transcript-text"
                  placeholder="Paste your interview transcript here or extract from a file..."
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  className="min-h-[500px] resize-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-blue-500"
                  style={{
                    outline: "none",
                  }}
                  disabled={isUploading}
                />
              </div>

              {uploadResult && (
                <Alert
                  variant={uploadResult.success ? "default" : "destructive"}
                  className="col-span-3"
                >
                  {uploadResult.success ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <AlertCircle className="h-4 w-4" />
                  )}
                  <AlertDescription>
                    {uploadResult.success ? (
                      <div className="space-y-1">
                        <p className="font-medium">{uploadResult.message}</p>
                        {uploadResult.change_summary && (
                          <p className="text-sm">
                            {uploadResult.change_summary}
                          </p>
                        )}
                        {uploadResult.word_count_raw &&
                          uploadResult.word_count_clean && (
                            <p className="text-sm">
                              Word count:{" "}
                              {uploadResult.word_count_raw.toLocaleString()} →{" "}
                              {uploadResult.word_count_clean.toLocaleString()}
                            </p>
                          )}
                      </div>
                    ) : (
                      uploadResult.error
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        {/* Fixed footer with word count and action buttons - outside scroll */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 pt-2 pb-1">
          <div className="flex justify-between items-center">
            {/* Word count information */}
            <div className="flex-1">
              {isViewMode ? (
                <>
                  {activeTab === "original" && editedOriginalText && (
                    <p className="text-sm text-muted-foreground">
                      Word count:{" "}
                      {editedOriginalText
                        .split(/\s+/)
                        .filter((w) => w.length > 0)
                        .length.toLocaleString()}
                    </p>
                  )}
                  {activeTab === "cleaned" &&
                    hasCleanedTranscript &&
                    existingTranscript?.text_clean && (
                      <div className="flex gap-4">
                        <p className="text-sm text-muted-foreground">
                          Word count:{" "}
                          {formatTranscriptText(existingTranscript.text_clean)
                            .split(/\s+/)
                            .filter((w) => w.length > 0)
                            .length.toLocaleString()}
                        </p>
                        {existingTranscript?.meta?.word_count_raw &&
                          existingTranscript?.meta?.word_count_clean && (
                            <p className="text-sm text-muted-foreground">
                              Change:{" "}
                              {existingTranscript.meta.word_count_raw.toLocaleString()}{" "}
                              →{" "}
                              {existingTranscript.meta.word_count_clean.toLocaleString()}
                            </p>
                          )}
                      </div>
                    )}
                </>
              ) : (
                // Upload mode word count
                rawText && (
                  <p className="text-sm text-muted-foreground">
                    Word count: {wordCount.toLocaleString()}
                  </p>
                )
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose}>
                {isViewMode ? "Close" : "Cancel"}
              </Button>
              {!isViewMode && (
                <Button
                  onClick={() => handleUpload()}
                  disabled={isUploading || !rawText.trim()}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Starting upload...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload & Clean
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
